// Chemin: C:\gestion-copro\src\services\pdfCourrier.service.ts
// Session 126 : Adaptation VIGIE — suppression sceau CS, signature adaptée copropriétaire individuel
// Session 135 : Suppression définitive du sceau Base64 (~42Ko) — plus utilisé

import jsPDF from 'jspdf';
import { Courrier, TYPE_COURRIER_LABELS, MODE_ENVOI_LABELS } from '../types/courrier.types';

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  marginLeft: 25,
  marginRight: 25,
  marginTop: 20,
  marginBottom: 20,
  pageWidth: 210,
  pageHeight: 297,
  fontSizeNormal: 11,
  fontSizeSmall: 9,
  fontSizeLarge: 12,
  fontSizeTitle: 14,
  lineHeight: 6,
  paragraphSpacing: 8
};

interface PDFCourrierOptions {
  courrier: Courrier;
  nomCopropriete: string;
  adresseCopropriete?: string;
  emailCS?: string;
  /** Session 126 : true si courrier VIGIE (copropriétaire individuel) */
  isVigie?: boolean;
  /** Session 126 : Nom complet du signataire (ex: "Nicole FOUQUET, Propriétaire lot n°5") */
  nomSignataire?: string;
}

const formatDateFr = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateCourte = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const cleanTextForPDF = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    .replace(/["\u201C\u201D]/g, '"')
    .replace(/['\u2018\u2019]/g, "'")
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
};

const splitTextToLines = (doc: jsPDF, text: string, maxWidth: number): string[] => {
  const cleanText = cleanTextForPDF(text);
  return doc.splitTextToSize(cleanText, maxWidth);
};

export const genererPDFCourrier = async (options: PDFCourrierOptions): Promise<Blob> => {
  const { courrier, nomCopropriete, adresseCopropriete, emailCS, isVigie = false, nomSignataire } = options;
  
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const contentWidth = CONFIG.pageWidth - CONFIG.marginLeft - CONFIG.marginRight;
  let yPos = CONFIG.marginTop;
  
  // EN-TÊTE EXPÉDITEUR
  doc.setFontSize(CONFIG.fontSizeLarge);
  doc.setFont('helvetica', 'bold');
  // Session 126 : VIGIE = nom du copropriétaire, CS = "Conseil Syndical"
  const enteteExpediteur = isVigie 
    ? cleanTextForPDF(nomSignataire || courrier.redacteurNom || 'Coproprietaire')
    : 'Conseil Syndical';
  doc.text(enteteExpediteur, CONFIG.marginLeft, yPos);
  yPos += CONFIG.lineHeight;
  
  doc.setFontSize(CONFIG.fontSizeNormal);
  doc.setFont('helvetica', 'normal');
  doc.text(cleanTextForPDF(nomCopropriete), CONFIG.marginLeft, yPos);
  yPos += CONFIG.lineHeight;
  
  if (adresseCopropriete) {
    const adresseLines = splitTextToLines(doc, adresseCopropriete, contentWidth / 2);
    adresseLines.forEach(line => {
      doc.text(line, CONFIG.marginLeft, yPos);
      yPos += CONFIG.lineHeight;
    });
  }
  
  if (emailCS) {
    doc.setFontSize(CONFIG.fontSizeSmall);
    doc.text(cleanTextForPDF(emailCS), CONFIG.marginLeft, yPos);
    yPos += CONFIG.lineHeight;
  }
  
  // DATE
  yPos += CONFIG.paragraphSpacing;
  doc.setFontSize(CONFIG.fontSizeNormal);
  doc.setFont('helvetica', 'normal');
  const dateText = `Le ${formatDateFr(courrier.dateValidation || courrier.dateCreation)}`;
  const dateWidth = doc.getTextWidth(dateText);
  doc.text(dateText, CONFIG.pageWidth - CONFIG.marginRight - dateWidth, yPos);
  yPos += CONFIG.paragraphSpacing * 2;
  
  // DESTINATAIRE
  const destX = CONFIG.pageWidth - CONFIG.marginRight - 80;
  doc.setFont('helvetica', 'bold');
  doc.text(cleanTextForPDF(courrier.destinataire.nom), destX, yPos);
  yPos += CONFIG.lineHeight;
  
  doc.setFont('helvetica', 'normal');
  if (courrier.destinataire.adresse) {
    const adresseDestLines = splitTextToLines(doc, courrier.destinataire.adresse, 80);
    adresseDestLines.forEach(line => {
      doc.text(line, destX, yPos);
      yPos += CONFIG.lineHeight;
    });
  }
  yPos += CONFIG.paragraphSpacing * 2;
  
  // RÉFÉRENCE ET TYPE
  doc.setFontSize(CONFIG.fontSizeSmall);
  doc.setFont('helvetica', 'normal');
  doc.text(`Ref: ${courrier.numero}`, CONFIG.marginLeft, yPos);
  const typeText = `${TYPE_COURRIER_LABELS[courrier.type]} - ${MODE_ENVOI_LABELS[courrier.modeEnvoi]}`;
  const typeWidth = doc.getTextWidth(typeText);
  doc.text(typeText, CONFIG.pageWidth - CONFIG.marginRight - typeWidth, yPos);
  yPos += CONFIG.paragraphSpacing;
  
  doc.setLineWidth(0.5);
  doc.line(CONFIG.marginLeft, yPos, CONFIG.pageWidth - CONFIG.marginRight, yPos);
  yPos += CONFIG.paragraphSpacing;
  
  // OBJET
  doc.setFontSize(CONFIG.fontSizeNormal);
  doc.setFont('helvetica', 'bold');
  doc.text('Objet :', CONFIG.marginLeft, yPos);
  doc.setFont('helvetica', 'normal');
  const objetLines = splitTextToLines(doc, courrier.objet, contentWidth - 20);
  doc.text(objetLines, CONFIG.marginLeft + 18, yPos);
  yPos += objetLines.length * CONFIG.lineHeight;
  yPos += CONFIG.paragraphSpacing;
  
  // CORPS DU COURRIER
  doc.setFontSize(CONFIG.fontSizeNormal);
  doc.setFont('helvetica', 'normal');
  const paragraphes = courrier.contenu.split('\n\n');
  
  for (const paragraphe of paragraphes) {
    if (!paragraphe.trim()) continue;
    if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 40) {
      doc.addPage();
      yPos = CONFIG.marginTop;
    }
    const lignes = splitTextToLines(doc, paragraphe.replace(/\n/g, ' '), contentWidth);
    for (const ligne of lignes) {
      if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 20) {
        doc.addPage();
        yPos = CONFIG.marginTop;
      }
      doc.text(ligne, CONFIG.marginLeft, yPos);
      yPos += CONFIG.lineHeight;
    }
    yPos += CONFIG.paragraphSpacing / 2;
  }
  
  // SIGNATURE (Session 135 : sceau supprimé définitivement)
  const espaceSignatureRequis = 40;
  if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - espaceSignatureRequis) {
    doc.addPage();
    yPos = CONFIG.marginTop;
  }
  yPos += CONFIG.paragraphSpacing * 2;
  
  doc.setFont('helvetica', 'bold');
  // Session 126 : VIGIE = signature personnelle, CS = signature conseil syndical
  const signature = isVigie
    ? cleanTextForPDF(nomSignataire || courrier.redacteurNom || 'Coproprietaire')
    : `Le Conseil Syndical de ${cleanTextForPDF(nomCopropriete)}`;
  const signatureLines = splitTextToLines(doc, signature, 80);
  const signatureX = CONFIG.pageWidth - CONFIG.marginRight - 80;
  
  signatureLines.forEach(line => {
    doc.text(line, signatureX, yPos);
    yPos += CONFIG.lineHeight;
  });
  
  // PIED DE PAGE
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(CONFIG.fontSizeSmall);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    const pageText = `Page ${i}/${totalPages}`;
    const pageWidth = doc.getTextWidth(pageText);
    doc.text(pageText, (CONFIG.pageWidth - pageWidth) / 2, CONFIG.pageHeight - 10);
    doc.setTextColor(0, 0, 0);
  }
  
  return doc.output('blob');
};

export const telechargerPDFCourrier = async (options: PDFCourrierOptions): Promise<void> => {
  const blob = await genererPDFCourrier(options);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${options.courrier.numero}_${formatDateCourte(options.courrier.dateCreation)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ouvrirPDFCourrier = async (options: PDFCourrierOptions): Promise<void> => {
  const blob = await genererPDFCourrier(options);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};
